import React, { useState, useEffect } from "react";
import { toast } from 'react-toastify';
import AgregarVeterinarioModal from "./AgregarVeterinarioModal";
import ModalOverlay from "../../components/ModalOverlay";

const COLORS = {
  bgMain: '#f9fafb',
  bgCard: '#ffffff',
  bgSecondary: '#f8f9fa',
  textPrimary: '#1a1a1a',
  textSecondary: '#6c757d',
  textMuted: '#9ca3af',
  accent: '#6ab04c',
  accentDark: '#5a9a3c',
  accentGlow: 'rgba(106, 176, 76, 0.1)',
  border: 'rgba(0, 0, 0, 0.1)',
  borderLight: 'rgba(0, 0, 0, 0.05)',
  danger: '#ef4444',
  warning: '#f59e0b',
  success: '#22c55e',
};

export default function ExpedienteClinicoVeterinarioList({
  veterinarios = [],
  vetLimites = null,
  onCreateVet,
  onUpdateVet,
  onReInviteVet,
  onDeleteVet,
  onClose
}) {
  const [localVets, setLocalVets] = useState([]);
  const [selectedVet, setSelectedVet] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [deletingVetId, setDeletingVetId] = useState(null);
  const [savingVet, setSavingVet] = useState(false);

  useEffect(() => {
    setLocalVets(veterinarios);
  }, [veterinarios]);

  function handleAddVet() {
    setSelectedVet(null);
    setShowForm(true);
  }

  function handleEditVet(vet) {
    setSelectedVet(vet);
    setShowForm(true);
  }

  function handleCancel() {
    setShowForm(false);
    setSelectedVet(null);
  }

  async function handleSave(formData) {
    if (!formData.correo) {
      formData.estado = "sinCorreo";
    }
    setSavingVet(true);
    try {
      if (selectedVet && selectedVet.id) {
        await onUpdateVet(selectedVet.id, formData);
        setLocalVets((prev) =>
          prev.map((v) => (v.id === selectedVet.id ? { ...v, ...formData } : v))
        );
      } else {
        await onCreateVet(formData);
      }
      setShowForm(false);
      setSelectedVet(null);
    } finally {
      setSavingVet(false);
    }
  }

  async function handleReInvite(vet) {
    if (onReInviteVet) {
      await onReInviteVet(vet);
    }
    setShowForm(false);
    setSelectedVet(null);
  }

  async function handleDelete(vet) {
    const confirmacion = window.confirm(
      `¿Eliminar a ${vet.nombre}?\n\nSe removerá de todas tus mascotas. Podrás agregarlo nuevamente después.`
    );
    if (!confirmacion) return;

    setDeletingVetId(vet.id);
    try {
      if (onDeleteVet) {
        await onDeleteVet(vet.id);
      }
      setLocalVets((prev) => prev.filter((v) => v.id !== vet.id));
    } catch (error) {
      console.error("Error eliminando veterinario:", error);
      toast.error(`Error al eliminar: ${error.message || "Intenta nuevamente"}`);
    } finally {
      setDeletingVetId(null);
    }
  }

  function getEstadoBadge(estado = "pendiente") {
    switch (estado) {
      case "verificado":
        return { label: 'Conectado', icon: 'bi-check-circle-fill', bg: COLORS.accentGlow, color: COLORS.accent };
      case "pendiente":
        return { label: 'Pendiente', icon: 'bi-clock', bg: 'rgba(245, 158, 11, 0.1)', color: COLORS.warning };
      case "sinCorreo":
        return { label: 'Sin invitación', icon: 'bi-envelope-slash', bg: 'rgba(107, 114, 128, 0.1)', color: COLORS.textMuted };
      case "noAceptado":
        return { label: 'Rechazado', icon: 'bi-x-circle', bg: 'rgba(239, 68, 68, 0.1)', color: COLORS.danger };
      default:
        return { label: estado, icon: 'bi-info-circle', bg: 'rgba(107, 114, 128, 0.1)', color: COLORS.textSecondary };
    }
  }

  const limitesAlcanzados = vetLimites && vetLimites.activos >= vetLimites.maxActivos;
  const invitacionesAgotadas = vetLimites && vetLimites.invitacionesUsadas >= vetLimites.maxInvitacionesAnio;
  const addDisabled = limitesAlcanzados || invitacionesAgotadas;

  return (
    <div style={{ maxWidth: '700px', margin: 'auto', backgroundColor: COLORS.bgMain, minHeight: '100%' }}>
      {/* Header */}
      <div style={{
        padding: '1.5rem',
        background: `linear-gradient(135deg, ${COLORS.accent}, ${COLORS.accentDark})`,
        borderRadius: '12px',
        marginBottom: '1.25rem',
        position: 'relative',
        color: '#fff',
      }}>
        <h3 style={{ margin: 0, fontSize: '1.375rem', fontWeight: 700 }}>
          <i className="bi bi-heart-pulse me-2"></i>
          Módulo Veterinario
        </h3>
        <p style={{ margin: '0.25rem 0 0', opacity: 0.85, fontSize: '0.85rem' }}>
          Administra aquí tus veterinarios
        </p>
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            style={{
              position: 'absolute', top: '1rem', right: '1rem',
              background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: '50%',
              width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', color: '#fff', fontSize: '1.1rem',
            }}
          >
            <i className="bi bi-x-lg"></i>
          </button>
        )}
      </div>

      {/* Main card */}
      <div style={{
        backgroundColor: COLORS.bgCard,
        borderRadius: '12px',
        border: `1px solid ${COLORS.border}`,
        boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
        overflow: 'hidden',
      }}>
        {/* Sub-header with limits */}
        <div style={{
          padding: '1rem 1.25rem',
          borderBottom: `1px solid ${COLORS.borderLight}`,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem',
        }}>
          <h5 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 600, color: COLORS.textPrimary }}>
            Mis Veterinarios
          </h5>
          {vetLimites && (
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: '0.25rem',
                padding: '0.2rem 0.5rem', borderRadius: '20px', fontSize: '0.7rem', fontWeight: 500,
                backgroundColor: COLORS.accentGlow, color: COLORS.accent,
              }}>
                <i className="bi bi-people"></i>
                {vetLimites.activos}/{vetLimites.maxActivos} activos
              </span>
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: '0.25rem',
                padding: '0.2rem 0.5rem', borderRadius: '20px', fontSize: '0.7rem', fontWeight: 500,
                backgroundColor: 'rgba(107, 114, 128, 0.1)', color: COLORS.textSecondary,
              }}>
                <i className="bi bi-envelope"></i>
                {vetLimites.invitacionesUsadas}/{vetLimites.maxInvitacionesAnio} invitaciones/año
              </span>
            </div>
          )}
        </div>

        <div style={{ padding: '1.25rem' }}>
          {/* Alertas */}
          {limitesAlcanzados && (
            <div style={{
              backgroundColor: 'rgba(245, 158, 11, 0.1)', borderRadius: '8px',
              padding: '0.75rem', marginBottom: '1rem', fontSize: '0.85rem', color: '#92400e'
            }}>
              <i className="bi bi-exclamation-triangle me-2"></i>
              Has alcanzado el límite de {vetLimites.maxActivos} veterinarios activos.
            </div>
          )}
          {invitacionesAgotadas && !limitesAlcanzados && (
            <div style={{
              backgroundColor: 'rgba(245, 158, 11, 0.1)', borderRadius: '8px',
              padding: '0.75rem', marginBottom: '1rem', fontSize: '0.85rem', color: '#92400e'
            }}>
              <i className="bi bi-exclamation-triangle me-2"></i>
              Has usado tus {vetLimites.maxInvitacionesAnio} invitaciones de este año.
              <br /><small><a href="mailto:soporte@lemmontags.com">Contacta a soporte</a></small>
            </div>
          )}

          {/* Botón agregar */}
          <button
            onClick={handleAddVet}
            disabled={addDisabled}
            style={{
              width: '100%', padding: '0.75rem', marginBottom: '1.25rem',
              backgroundColor: addDisabled ? COLORS.bgSecondary : COLORS.accent,
              color: addDisabled ? COLORS.textMuted : '#fff',
              border: 'none', borderRadius: '10px', fontSize: '0.9rem', fontWeight: 600,
              cursor: addDisabled ? 'not-allowed' : 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem',
              transition: 'all 0.2s',
            }}
          >
            <i className="bi bi-person-plus"></i>
            Agregar Veterinario
          </button>

          {/* Lista de vets */}
          {localVets.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '2rem 1rem', color: COLORS.textMuted }}>
              <i className="bi bi-people" style={{ fontSize: '2rem', display: 'block', marginBottom: '0.5rem' }}></i>
              <p style={{ margin: 0, fontSize: '0.9rem' }}>No hay veterinarios registrados</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {localVets.map((vet) => {
                const badge = getEstadoBadge(vet.estado);
                return (
                  <div key={vet.id} style={{
                    backgroundColor: COLORS.bgSecondary,
                    borderRadius: '10px',
                    border: `1px solid ${COLORS.borderLight}`,
                    overflow: 'hidden',
                  }}>
                    {/* Info */}
                    <div style={{ padding: '1rem 1.25rem' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
                        <div>
                          <h6 style={{ margin: 0, fontSize: '1rem', fontWeight: 600, color: COLORS.textPrimary }}>
                            {vet.nombre || 'Sin Nombre'}
                          </h6>
                          {vet.nombreClinica && (
                            <small style={{ color: COLORS.textSecondary }}>
                              <i className="bi bi-building me-1"></i>{vet.nombreClinica}
                            </small>
                          )}
                        </div>
                        <span style={{
                          display: 'inline-flex', alignItems: 'center', gap: '0.25rem',
                          padding: '0.2rem 0.5rem', borderRadius: '20px', fontSize: '0.7rem', fontWeight: 500,
                          backgroundColor: badge.bg, color: badge.color,
                          flexShrink: 0,
                        }}>
                          <i className={`bi ${badge.icon}`}></i>
                          {badge.label}
                        </span>
                      </div>

                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', fontSize: '0.8rem', color: COLORS.textSecondary }}>
                        {vet.telefono && (
                          <a href={`tel:${vet.telefono}`} style={{ color: COLORS.accent, textDecoration: 'none' }}>
                            <i className="bi bi-telephone me-1"></i>{vet.telefono}
                          </a>
                        )}
                        {vet.correo && (
                          <span><i className="bi bi-envelope me-1"></i>{vet.correo}</span>
                        )}
                        {vet.direccion && (
                          <span><i className="bi bi-geo-alt me-1"></i>{vet.direccion}</span>
                        )}
                        {vet.especialidad && (
                          <span><i className="bi bi-award me-1"></i>{vet.especialidad}</span>
                        )}
                      </div>
                    </div>

                    {/* Actions */}
                    <div style={{
                      padding: '0.5rem 1.25rem',
                      borderTop: `1px solid ${COLORS.borderLight}`,
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      backgroundColor: 'rgba(0,0,0,0.015)',
                    }}>
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        {vet.estado !== "verificado" && (
                          <button
                            onClick={() => handleEditVet(vet)}
                            style={{
                              background: 'none', border: 'none', cursor: 'pointer',
                              color: COLORS.textSecondary, fontSize: '0.8rem', padding: '0.25rem 0.5rem',
                              borderRadius: '6px',
                            }}
                          >
                            <i className="bi bi-pencil me-1"></i>Editar
                          </button>
                        )}
                        {vet.estado !== "verificado" && vet.estado !== "sinCorreo" && (
                          <button
                            onClick={() => handleReInvite(vet)}
                            style={{
                              background: 'none', border: 'none', cursor: 'pointer',
                              color: '#3b82f6', fontSize: '0.8rem', padding: '0.25rem 0.5rem',
                              borderRadius: '6px',
                            }}
                          >
                            <i className="bi bi-arrow-clockwise me-1"></i>Reinvitar
                          </button>
                        )}
                      </div>

                      <button
                        onClick={() => handleDelete(vet)}
                        disabled={deletingVetId === vet.id}
                        style={{
                          background: 'none', border: 'none',
                          cursor: deletingVetId === vet.id ? 'wait' : 'pointer',
                          color: COLORS.danger, fontSize: '0.8rem', padding: '0.25rem 0.5rem',
                          borderRadius: '6px',
                        }}
                      >
                        {deletingVetId === vet.id ? (
                          <><span className="spinner-border spinner-border-sm me-1"></span>...</>
                        ) : (
                          <><i className="bi bi-trash me-1"></i>Eliminar</>
                        )}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Modal para crear/editar */}
      {showForm && (
        <ModalOverlay
          onClose={handleCancel}
          containerStyle={{
            padding: "0",
            width: "90%",
            maxWidth: "550px",
            borderRadius: '12px',
            backgroundColor: 'transparent',
            border: 'none',
            boxShadow: 'none',
          }}
        >
          <AgregarVeterinarioModal
            veterinarianData={selectedVet}
            limites={vetLimites}
            onSave={handleSave}
            onCancel={handleCancel}
            onReInvite={handleReInvite}
            isLoading={savingVet}
          />
        </ModalOverlay>
      )}
    </div>
  );
}
